import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import SynioLogo from '../SynioLogo';

const AuthForm = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const { signIn, signUp } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!email || !password) {
      setError('Bitte füllen Sie alle Felder aus.');
      return;
    }

    if (!isLogin && password !== confirmPassword) {
      setError('Die Passwörter stimmen nicht überein.');
      return;
    }

    if (!isLogin && password.length < 6) {
      setError('Das Passwort muss mindestens 6 Zeichen lang sein.');
      return;
    }

    setLoading(true);

    try {
      if (isLogin) {
        const { error } = await signIn(email, password);
        if (error) {
          // Spezifische Fehlerbehandlung
          if (error.message?.includes('secret') || error.message?.includes('service_role')) {
            setError(
              '⚠️ Konfigurationsfehler: Der falsche API-Schlüssel wird verwendet.\n\n' +
              'Bitte verwenden Sie den "anon" oder "public" Key aus Ihrem Supabase-Dashboard, ' +
              'nicht den "service_role" Key.\n\n' +
              'Gehen Sie zu: Supabase Dashboard → Settings → API → anon/public key'
            );
          } else {
            setError(error.message || 'Anmeldung fehlgeschlagen.');
          }
        }
      } else {
        const { error } = await signUp(email, password);
        if (error) {
          // Spezifische Fehlerbehandlung
          if (error.message?.includes('secret') || error.message?.includes('service_role')) {
            setError(
              '⚠️ Konfigurationsfehler: Der falsche API-Schlüssel wird verwendet.\n\n' +
              'Bitte verwenden Sie den "anon" oder "public" Key aus Ihrem Supabase-Dashboard, ' +
              'nicht den "service_role" Key.\n\n' +
              'Gehen Sie zu: Supabase Dashboard → Settings → API → anon/public key'
            );
          } else {
            setError(error.message || 'Registrierung fehlgeschlagen.');
          }
        } else {
          setMessage('Registrierung erfolgreich! Bitte überprüfen Sie Ihre E-Mail, um Ihr Konto zu bestätigen.');
        }
      }
    } catch (err) {
      if (err.message?.includes('secret') || err.message?.includes('service_role')) {
        setError(
          '⚠️ Konfigurationsfehler: Der falsche API-Schlüssel wird verwendet.\n\n' +
          'Bitte verwenden Sie den "anon" oder "public" Key aus Ihrem Supabase-Dashboard, ' +
          'nicht den "service_role" Key.\n\n' +
          'Gehen Sie zu: Supabase Dashboard → Settings → API → anon/public key'
        );
      } else {
        setError('Ein unerwarteter Fehler ist aufgetreten: ' + (err.message || 'Unbekannter Fehler'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="mb-8 flex justify-center">
          <SynioLogo height={60} />
        </div>

        {/* Auth Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
          <h2 className="text-2xl font-bold text-center text-gray-900 mb-6">
            {isLogin ? 'Anmelden' : 'Registrieren'}
          </h2>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm whitespace-pre-line">
              {error}
            </div>
          )}

          {message && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
              {message}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                E-Mail
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                placeholder="ihre@email.de"
                required
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Passwort
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                placeholder="••••••••"
                required
              />
            </div>

            {!isLogin && (
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Passwort bestätigen
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                  placeholder="••••••••"
                  required
                />
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 bg-gray-900 hover:bg-gray-800 text-white font-medium rounded-lg transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2"
            >
              {loading ? 'Wird verarbeitet...' : isLogin ? 'Anmelden' : 'Registrieren'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => {
                setIsLogin(!isLogin);
                setError('');
                setMessage('');
                setPassword('');
                setConfirmPassword('');
              }}
              className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              {isLogin ? (
                <>
                  Noch kein Konto? <span className="font-semibold">Registrieren</span>
                </>
              ) : (
                <>
                  Bereits ein Konto? <span className="font-semibold">Anmelden</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthForm;

